import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import withModal from '../../../../../components/global/modal/withModal';
import DismissButton from '../../../../../components/global/DismissButton';
import Button from '../../../../../components/global/Button';
import Badge from '../../../../../components/global/Badge';
import { getWaldogramRemainingCaptionChars } from '../../../../../components/global/WaldogramConfigurator/helpers';
import useVirtualPageView from '../../../../../apps/pro/services/analytics/useVirtualPageView';
import {
  getCardElementTextFontFamily,
  TEXT_PRESET_LABELS,
  TEXT_PRESETS,
} from '../../../../../components/global/CardDesigner/components/CardDesignerCardElements/CardDesignerCardElementText/helpers';
import { isIOS } from '../../../../../util/device-detector';

const propTypes = {
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  title: PropTypes.string,
  cardElementConfiguration: PropTypes.shape({
    help_placeholder: PropTypes.string,
    color: PropTypes.string,
    font_family: PropTypes.string,
    text_align: PropTypes.string,
    font_size: PropTypes.string,
  }),
  defaultCardElementConfiguration: PropTypes.shape({
    help_placeholder: PropTypes.string,
    label: PropTypes.string,
  }),
};

const CardElementTextControl = ({
  onClose,
  onChange,
  defaultValue,
  title,
  cardElementConfiguration,
  defaultCardElementConfiguration,
}) => {
  useVirtualPageView({
    title: 'Create a card - Type your note',
    path: '/element/text',
  });

  const [text, setText] = useState(defaultValue ?? '');
  const textarea = useRef();
  useEffect(() => {
    if (textarea.current) {
      textarea.current.focus();
      textarea.current.setSelectionRange?.(text.length, text.length);
    }
  }, []);

  // TODO [cards] this config somehow needs to be dynamic.
  const config = {
    rows: 16,
    charsPerRow: 25,
  };
  const { remaining } = getWaldogramRemainingCaptionChars(config, text);

  const textPresetLabel = (() => {
    const isTextPreset = Object.keys(TEXT_PRESETS).some(
      k =>
        TEXT_PRESETS[k] === defaultCardElementConfiguration?.help_placeholder,
    );

    if (isTextPreset) {
      return TEXT_PRESET_LABELS[
        defaultCardElementConfiguration?.help_placeholder
      ];
    }

    return '';
  })();

  return (
    <div className="card-element-text-control-content">
      <div className="card-element-control-header">
        <DismissButton onClick={onClose} />

        <span className="card-element-control-header-title">
          {title ||
            textPresetLabel ||
            defaultCardElementConfiguration?.label ||
            defaultCardElementConfiguration?.help_placeholder ||
            cardElementConfiguration.help_placeholder ||
            'Type your message…'}
        </span>

        <Button
          btnStyle="link"
          onClick={() => {
            onClose();
            onChange(text);
          }}
        >
          Done
        </Button>
      </div>

      <textarea
        cols="30"
        rows="10"
        className="card-element-text-control-textarea"
        ref={textarea}
        value={text}
        style={{
          // Note: We don't force the text color here as it's always on a white background
          fontFamily: getCardElementTextFontFamily(cardElementConfiguration),
          textAlign:
            cardElementConfiguration.text_align?.toLowerCase?.() ?? undefined,
          fontWeight: cardElementConfiguration.font_weight ?? undefined,
          fontStyle:
            cardElementConfiguration.font_style?.toLowerCase() ?? undefined,
        }}
        onChange={event => {
          const message = event.target.value;
          const { remaining: latestRemaining } =
            getWaldogramRemainingCaptionChars(config, message);

          if (latestRemaining >= 0) {
            setText(event.target.value);
          }
        }}
        onBlur={event => {
          // Best way we have right now to detect whether the "Done"
          // button on the mobile safari keyboard is pressed.
          if (!event.relatedTarget && isIOS()) {
            onChange(text);
            onClose();
          }
        }}
      />

      {
        // TODO [cards] temporarily disabled until we find a way to make this properly dynamic
        false && (
          <Badge round className="card-element-text-control-char-count">
            <b>{remaining}</b> chars remaining
          </Badge>
        )
      }
    </div>
  );
};

CardElementTextControl.propTypes = propTypes;

export const CARD_ELEMENT_TEXT_CONTROL_MODAL =
  'CARD_ELEMENT_TEXT_CONTROL_MODAL';

export default withModal(CardElementTextControl, {
  name: CARD_ELEMENT_TEXT_CONTROL_MODAL,
  dismissible: false,
  size: 'xs',
  type: 'full',
  hideHeader: true,
  className: 'card-element-text-control-modal',
});
